import './link.scss';

import Icon, { IconProps } from '../icon/Icon';
import Tooltip, { TooltipProps } from '../tooltip/tooltip';

import classNames from 'classnames';

export enum LinkType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  DESTRUCTIVE = 'destructive',
}

export enum LinkState {
  DEFAULT = 'default',
  HOVERED = 'hovered',
  PRESSED = 'pressed',
  ACTIVE = 'active',
  FOCUSED = 'focused',
  DISABLED = 'disabled',
}

export enum LinkVariant {
  LINK = 'link',
  BUTTON = 'button',
  SPAN = 'span',
}

export interface LinkProps {
  href?: string;
  label?: string;
  icon?: IconProps;
  iconTooltip?: TooltipProps;
  type?: LinkType;
  isSmall?: boolean;
  state?: LinkState;
  description?: string;
  className?: string;
  variant?: LinkVariant;
  slot?: string;
  dataDialogId?: string;
  onClick?: (e) => void;
  ariaExpanded?: boolean;
  ariaControls?: string;
  iconPosition?: 'left' | 'right';
}

const Link = ({
  href,
  label,
  icon,
  iconPosition = 'left',
  iconTooltip,
  type = LinkType.PRIMARY,
  isSmall = false,
  state = LinkState.DEFAULT,
  description,
  className,
  variant = LinkVariant.LINK,
  slot,
  dataDialogId,
  onClick,
  ariaExpanded,
  ariaControls,
}: LinkProps) => {
  if (variant === LinkVariant.BUTTON) {
    return (
      <button
        type="button"
        className={classNames(
          'noselect',
          'a-link',
          `a-link--${type}`,
          {
            'a-link--small': isSmall,
            hover: state === LinkState.HOVERED,
            pressed: state === LinkState.PRESSED,
            active: state === LinkState.ACTIVE,
            focus: state === LinkState.FOCUSED,
            disabled: state === LinkState.DISABLED,
          },
          className
        )}
        disabled={state === LinkState.DISABLED}
        aria-label={description}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        slot={slot}
        data-dialog-id={dataDialogId}
        onClick={onClick}
      >
        {icon &&
          iconPosition === 'left' &&
          (iconTooltip ? (
            <Tooltip
              {...iconTooltip}
              children={
                <Icon
                  iconType={icon.iconType}
                  className={icon.className ?? 'a-link__icon'}
                />
              }
            />
          ) : (
            <Icon iconType={icon.iconType} className="a-link__icon" />
          ))}
        {label && <span className="a-link__text">{label}</span>}
        {icon &&
          iconPosition === 'right' &&
          (iconTooltip ? (
            <Tooltip
              {...iconTooltip}
              children={
                <Icon
                  iconType={icon.iconType}
                  className={icon.className ?? 'a-link__icon'}
                />
              }
            />
          ) : (
            <Icon iconType={icon.iconType} className="a-link__icon" />
          ))}
      </button>
    );
  }

  if (variant === LinkVariant.SPAN) {
    return (
      <span
        className={classNames({
          'a-link': true,
          [`a-link--${type}`]: type,
          'a-link--small': isSmall,
          hover: state === LinkState.HOVERED,
          pressed: state === LinkState.PRESSED,
          active: state === LinkState.ACTIVE,
          focus: state === LinkState.FOCUSED,
          disabled: state === LinkState.DISABLED,
          className,
        })}
        aria-disabled={state === LinkState.DISABLED}
        aria-alive={state === LinkState.DISABLED ? 'polite' : undefined}
      >
        {icon &&
          iconPosition === 'left' &&
          (iconTooltip ? (
            <Tooltip
              {...iconTooltip}
              children={
                <Icon
                  iconType={icon.iconType}
                  className={icon.className ?? 'a-link__icon'}
                />
              }
            />
          ) : (
            <Icon iconType={icon.iconType} className="a-link__icon" />
          ))}
        {label && <span className="a-link__text">{label}</span>}
        {icon &&
          iconPosition === 'right' &&
          (iconTooltip ? (
            <Tooltip
              {...iconTooltip}
              children={
                <Icon
                  iconType={icon.iconType}
                  className={icon.className ?? 'a-link__icon'}
                />
              }
            />
          ) : (
            <Icon iconType={icon.iconType} className="a-link__icon" />
          ))}
      </span>
    );
  }

  return (
    <a
      href={state !== LinkState.DISABLED ? href : undefined}
      className={classNames({
        'a-link': true,
        [`a-link--${type}`]: type,
        'a-link--small': isSmall,
        hover: state === LinkState.HOVERED,
        pressed: state === LinkState.PRESSED,
        active: state === LinkState.ACTIVE,
        focus: state === LinkState.FOCUSED,
        disabled: state === LinkState.DISABLED,
        className,
      })}
      role={state === LinkState.DISABLED ? 'link' : undefined}
      aria-label={description}
    >
      {icon &&
        iconPosition === 'left' &&
        (iconTooltip ? (
          <Tooltip
            {...iconTooltip}
            children={
              <Icon
                iconType={icon.iconType}
                className={icon.className ?? 'a-link__icon'}
              />
            }
          />
        ) : (
          <Icon iconType={icon.iconType} className="a-link__icon" />
        ))}
      {label && <span className="a-link__text">{label}</span>}
      {icon &&
        iconPosition === 'right' &&
        (iconTooltip ? (
          <Tooltip
            {...iconTooltip}
            children={
              <Icon
                iconType={icon.iconType}
                className={icon.className ?? 'a-link__icon'}
              />
            }
          />
        ) : (
          <Icon iconType={icon.iconType} className="a-link__icon" />
        ))}
    </a>
  );
};

export default Link;
